/* Reusable module × action permission grid.
   - Editable mode: checkboxes with per-row, per-column and global toggles.
   - Read-only mode: ✓ / · indicators (used on the user details page).
   Value shape matches the registry: { [moduleKey]: { [actionKey]: boolean } }. */

import React from "react";
import s from "./userManagement.module.css";
import {
  MODULES,
  ACTIONS,
  normalizePermissions,
  countGranted,
  TOTAL_PERMISSION_COUNT,
} from "../../rbac/permissions";

const PermissionMatrix = ({ value, onChange, readOnly = false }) => {
  const perms = normalizePermissions(value);
  const granted = countGranted(perms);

  const isApplicable = (moduleKey, action) => {
    const mod = MODULES.find((m) => m.key === moduleKey);
    return mod?.actions.includes(action);
  };

  const setCell = (moduleKey, action, checked) => {
    if (readOnly) return;
    const next = normalizePermissions(perms);
    next[moduleKey][action] = checked;
    onChange(next);
  };

  const toggleRow = (moduleKey) => {
    if (readOnly) return;
    const mod = MODULES.find((m) => m.key === moduleKey);
    const allOn = mod.actions.every((a) => perms[moduleKey][a]);
    const next = normalizePermissions(perms);
    mod.actions.forEach((a) => (next[moduleKey][a] = !allOn));
    onChange(next);
  };

  const toggleColumn = (action) => {
    if (readOnly) return;
    const applicable = MODULES.filter((m) => m.actions.includes(action));
    const allOn = applicable.every((m) => perms[m.key][action]);
    const next = normalizePermissions(perms);
    applicable.forEach((m) => (next[m.key][action] = !allOn));
    onChange(next);
  };

  const setAll = (checked) => {
    if (readOnly) return;
    const next = normalizePermissions(perms);
    MODULES.forEach((m) => m.actions.forEach((a) => (next[m.key][a] = checked)));
    onChange(next);
  };

  return (
    <div className={s.matrixCard}>
      <div className={s.matrixToolbar}>
        <div className={s.matrixToolbarInfo}>
          <strong>{granted}</strong> of {TOTAL_PERMISSION_COUNT} permissions
          granted
        </div>
        {!readOnly && (
          <div className={s.matrixToolbarActions}>
            <button type="button" className={s.linkBtn} onClick={() => setAll(true)}>
              Grant all
            </button>
            <button type="button" className={s.linkBtn} onClick={() => setAll(false)}>
              Clear all
            </button>
          </div>
        )}
      </div>

      <div className={s.matrixScroll}>
        <table className={s.matrix}>
          <thead>
            <tr>
              <th className={s.matrixModuleCol}>Module</th>
              {ACTIONS.map((action) => (
                <th
                  key={action.key}
                  className={!readOnly ? s.matrixColHead : undefined}
                  title={
                    readOnly
                      ? action.description
                      : `${action.description} — click to toggle column`
                  }
                  onClick={!readOnly ? () => toggleColumn(action.key) : undefined}
                >
                  {action.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MODULES.map((mod) => (
              <tr key={mod.key} className={s.matrixRow}>
                <td className={s.matrixModuleCell}>
                  <div className={s.matrixModuleName}>
                    {mod.label}
                    {!readOnly && (
                      <button
                        type="button"
                        className={s.matrixRowToggle}
                        onClick={() => toggleRow(mod.key)}
                      >
                        toggle
                      </button>
                    )}
                  </div>
                  <div className={s.matrixModuleDesc}>{mod.description}</div>
                </td>
                {ACTIONS.map((action) => {
                  const applicable = isApplicable(mod.key, action.key);
                  if (!applicable) {
                    return (
                      <td key={action.key}>
                        <span className={s.matrixDash}>—</span>
                      </td>
                    );
                  }
                  const checked = Boolean(perms[mod.key][action.key]);
                  if (readOnly) {
                    return (
                      <td key={action.key} className={s.matrixReadCell}>
                        <span
                          className={checked ? s.matrixYes : s.matrixNo}
                          title={`${action.label} ${mod.label}: ${action.description}`}
                        >
                          {checked ? "✓" : "·"}
                        </span>
                      </td>
                    );
                  }
                  return (
                    <td key={action.key}>
                      <input
                        type="checkbox"
                        className={s.matrixCheckbox}
                        checked={checked}
                        onChange={(e) =>
                          setCell(mod.key, action.key, e.target.checked)
                        }
                        aria-label={`${mod.label} — ${action.label}`}
                        title={`${action.label} ${mod.label}: ${action.description}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PermissionMatrix;
